import { useState } from 'react';
import { motion } from 'motion/react';
import mailchimpService from '../../services/mailchimp';

interface PreRegisterFormProps {
  onSuccess?: () => void;
  className?: string;
  buttonText?: string;
  buttonColor?: string;
}

export default function PreRegisterForm({ 
  onSuccess, 
  className = "",
  buttonText = "Pre-regístrate gratis",
  buttonColor = "#FF7043"
}: PreRegisterFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setError('Por favor ingresa tu email');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setError('Por favor ingresa un email válido');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await mailchimpService.subscribeUser(
        formData.email.trim(),
        formData.name.trim() || undefined
      );

      if (result.success) {
        setSuccessMessage('¡Perfecto! Ya estás en nuestra lista VIP 🎉');
        setFormData({ name: '', email: '' });
        
        // Llamar callback de éxito si existe
        if (onSuccess) {
          setTimeout(onSuccess, 1500);
        }
      } else {
        setError(result.message);
        if (result.message.includes('ya está suscrito')) {
          setError('¡Genial! Ya estás en nuestra lista VIP 🎉');
        }
      }
    } catch (err) {
      console.error('Error durante la suscripción:', err);
      setError('Hubo un error inesperado. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            name="name"
            placeholder="Tu nombre (opcional)"
            value={formData.name}
            onChange={handleInputChange}
            className="flex-1 px-4 py-3 rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm focus:border-white focus:ring-2 focus:ring-white/20 outline-none transition-all duration-200 text-white placeholder-white/70"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          />
          <input
            type="email"
            name="email"
            placeholder="tu@email.com *"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="flex-1 px-4 py-3 rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm focus:border-white focus:ring-2 focus:ring-white/20 outline-none transition-all duration-200 text-white placeholder-white/70"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          />
        </div>
        
        {/* Mensaje de error */}
        {error && (
          <motion.div 
            className={`text-sm px-4 py-2 rounded-lg ${
              error.includes('🎉') ? 'text-green-700 bg-green-50 border border-green-200' : 'text-red-700 bg-red-50 border border-red-200'
            }`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.div>
        )}
        
        {/* Mensaje de éxito */}
        {successMessage && (
          <motion.div 
            className="text-green-700 text-sm bg-green-50 border border-green-200 px-4 py-2 rounded-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {successMessage}
          </motion.div>
        )}
        
        <motion.button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-orange-300 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed text-lg"
          style={{
            backgroundColor: buttonColor,
            fontFamily: 'Poppins, sans-serif'
          }}
          whileHover={!isSubmitting ? { scale: 1.02 } : {}}
          whileTap={!isSubmitting ? { scale: 0.98 } : {}}
          onMouseEnter={(e) => {
            if (!isSubmitting && buttonColor === '#FF7043') {
              e.currentTarget.style.backgroundColor = '#F4511E';
            } else if (!isSubmitting && buttonColor === '#F9C641') {
              e.currentTarget.style.backgroundColor = '#F9B233';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = buttonColor;
            }
          }}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Registrando...</span>
            </div>
          ) : (
            buttonText
          )}
        </motion.button>
      </form>
      
      <p className="text-sm text-white/70 mt-3 text-center">
        ✈️ Explora. Conecta. Descubre.
      </p>
    </div>
  );
}
